import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Edit, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => `R$ ${Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const PRICE_KEY = { varejo: "price_retail", atacado: "price_wholesale", especial: "price_special" };
const TABLE_LABEL = { varejo: "Varejo", atacado: "Atacado", especial: "Especial" };
const barColor = (p) => (p >= 100 ? "bg-green-500" : p >= 75 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500");

const Progress = ({ label, pct, achieved, target, remaining, testid }) => {
  const p = Math.min(pct || 0, 100);
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs"><span>{label}: <b>{achieved}</b> / {target}</span><span>{p.toFixed(1)}%</span></div>
      <div className="w-full bg-gray-200 rounded h-2 overflow-hidden"><div className={`h-2 ${barColor(p)}`} style={{ width: `${p}%` }} /></div>
      <p className="text-[11px]">Falta: <b className="text-red-700">{remaining}</b></p>
    </div>
  );
};

const ItemsProgress = ({ items, goalId }) => (
  <div className="border-t pt-1 space-y-1">
    {items.map((it) => {
      const p = Math.min(it.quantity_progress_percent || 0, 100);
      return (
        <div key={it.product_id} data-testid={`client-goal-item-${goalId}-${it.product_id}`}>
          <div className="flex justify-between text-[11px]"><b className="truncate">{it.product_name}</b><span>{p.toFixed(0)}%</span></div>
          <div className="w-full bg-gray-200 rounded h-1.5 overflow-hidden"><div className={`h-1.5 ${barColor(p)}`} style={{ width: `${p}%` }} /></div>
          <div className="text-[10px]">{it.achieved_quantity} / {it.target_quantity} {it.unit || ""} · {fmt(it.achieved_revenue)} / {fmt(it.target_revenue)}</div>
        </div>
      );
    })}
  </div>
);

const ItemsEditor = ({ items, products, priceType, onChange }) => {
  const upd = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const priceOf = (pid) => Number((products.find((p) => p.id === pid) || {})[PRICE_KEY[priceType]] || 0);
  const total = items.reduce((s, it) => s + priceOf(it.product_id) * (parseFloat(it.quantity) || 0), 0);
  return (
    <div className="space-y-2">
      <label className="text-xs font-bold">Produtos da meta *</label>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Select value={it.product_id || "none"} onValueChange={(v) => upd(i, "product_id", v === "none" ? "" : v)}>
            <SelectTrigger className="flex-1" data-testid={`client-goal-item-product-${i}`}><SelectValue placeholder="Produto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Selecione —</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id} disabled={items.some((x, j) => j !== i && x.product_id === p.id)}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" step="0.01" className="w-24" placeholder="Qtd" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} data-testid={`client-goal-item-qty-${i}`} />
          <span className="text-[11px] w-28 text-right" data-testid={`client-goal-item-value-${i}`}>{fmt(priceOf(it.product_id) * (parseFloat(it.quantity) || 0))}</span>
          <Button size="sm" variant="outline" className="text-red-600" disabled={items.length === 1} onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3" /></Button>
        </div>
      ))}
      <div className="flex justify-between items-center">
        <Button size="sm" variant="outline" onClick={() => onChange([...items, { product_id: "", quantity: "" }])} data-testid="client-goal-add-item"><Plus className="h-3 w-3 mr-1" />Adicionar produto</Button>
        <span className="text-sm">Valor da meta: <b data-testid="client-goal-total-value">{fmt(total)}</b></span>
      </div>
      <p className="text-[11px] text-gray-600">Valor calculado com o preço {TABLE_LABEL[priceType]} cadastrado no sistema.</p>
    </div>
  );
};

const GoalCard = ({ g, onEdit, onDelete }) => {
  const done = (g.progress_percent || 0) >= 100 && (g.quantity_progress_percent || 0) >= 100;
  return (
    <Card data-testid={`client-goal-${g.id}`} className={done ? "border-green-500 border-2" : ""}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-bold">{g.client_name}</p>
            <p className="text-xs">{String(g.month).padStart(2, "0")}/{g.year} · {(g.items || []).length} produto(s){g.price_type ? ` · ${TABLE_LABEL[g.price_type]}` : ""}</p>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => onEdit(g)} data-testid={`client-goal-edit-${g.id}`}><Edit className="h-3 w-3" /></Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDelete(g)} data-testid={`client-goal-delete-${g.id}`}><Trash2 className="h-3 w-3" /></Button>
          </div>
        </div>
        <div className="text-[11px] font-bold uppercase">Total da meta</div>
        <Progress label="Quantidade" pct={g.quantity_progress_percent} achieved={g.achieved_quantity} target={g.target_quantity} remaining={g.remaining_quantity} testid={`client-goal-qty-${g.id}`} />
        <Progress label="Valor" pct={g.progress_percent} achieved={fmt(g.achieved_revenue)} target={fmt(g.target_revenue)} remaining={fmt(g.remaining)} testid={`client-goal-rev-${g.id}`} />
        <ItemsProgress items={g.items || []} goalId={g.id} />
        {g.notes && <p className="text-xs italic">{g.notes}</p>}
      </CardContent>
    </Card>
  );
};

export default function ClientProductGoals({ companyId, goals, clients = [], products = [], onChanged }) {
  const [dlg, setDlg] = useState({ open: false, id: null, data: null });
  const setData = (patch) => setDlg((d) => ({ ...d, data: { ...d.data, ...patch } }));
  const now = new Date();

  const openNew = () => setDlg({ open: true, id: null, data: { year: now.getFullYear(), month: now.getMonth() + 1, client_id: "", price_type: "varejo", items: [{ product_id: "", quantity: "" }], notes: "" } });
  const openEdit = (g) => setDlg({ open: true, id: g.id, data: {
    year: g.year, month: g.month, client_id: g.client_id, price_type: g.price_type || "varejo", notes: g.notes || "",
    items: (g.items || []).map((i) => ({ product_id: i.product_id, quantity: String(i.target_quantity || "") })),
  } });

  const save = async () => {
    const d = dlg.data;
    const items = d.items.filter((i) => i.product_id && parseFloat(i.quantity) > 0).map((i) => ({ product_id: i.product_id, quantity: parseFloat(i.quantity) }));
    if (!d.client_id || items.length === 0) { toast.error("Selecione o cliente e pelo menos um produto com quantidade"); return; }
    const body = { year: parseInt(d.year), month: parseInt(d.month), client_id: d.client_id, price_type: d.price_type, items, notes: d.notes || null };
    try {
      if (dlg.id) await axios.put(`${API}/companies/${companyId}/factory/sales-goals/${dlg.id}`, body);
      else await axios.post(`${API}/companies/${companyId}/factory/sales-goals`, body);
      toast.success("Meta do cliente salva");
      setDlg({ open: false, id: null, data: null });
      onChanged();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  const remove = async (g) => {
    if (!window.confirm("Excluir esta meta do cliente?")) return;
    try {
      await axios.delete(`${API}/companies/${companyId}/factory/sales-goals/${g.id}`);
      toast.success("Meta excluída");
      onChanged();
    } catch (e) { toast.error("Erro ao excluir: " + (e.response?.data?.detail || "")); }
  };

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-sky-50" data-testid="client-product-goals">
      <div className="flex justify-between items-center gap-2">
        <h4 className="font-bold text-sm flex items-center gap-1"><Users className="h-4 w-4" />Metas Mensais por Cliente e Produto</h4>
        <Button size="sm" onClick={openNew} data-testid="new-client-goal-btn"><Plus className="h-3 w-3 mr-1" />Nova Meta de Cliente</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {goals.map((g) => <GoalCard key={g.id} g={g} onEdit={openEdit} onDelete={remove} />)}
        {goals.length === 0 && <p className="col-span-3 text-xs text-center py-3">Nenhuma meta por cliente cadastrada.</p>}
      </div>

      <Dialog open={dlg.open} onOpenChange={(o) => !o && setDlg({ open: false, id: null, data: null })}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="client-goal-dialog">
          <DialogHeader><DialogTitle>{dlg.id ? "Editar" : "Nova"} Meta de Cliente</DialogTitle></DialogHeader>
          {dlg.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold">Mês *</label>
                  <Select value={String(dlg.data.month)} onValueChange={(v) => setData({ month: parseInt(v) })}>
                    <SelectTrigger data-testid="client-goal-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{String(i + 1).padStart(2, "0")} - {m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold">Ano *</label>
                  <Input type="number" value={dlg.data.year} onChange={(e) => setData({ year: e.target.value })} data-testid="client-goal-year" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold">Cliente *</label>
                <Select value={dlg.data.client_id || "none"} onValueChange={(v) => setData({ client_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="client-goal-client"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Selecione —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold">Tabela de preço *</label>
                <Select value={dlg.data.price_type} onValueChange={(v) => setData({ price_type: v })}>
                  <SelectTrigger data-testid="client-goal-price-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="varejo">Varejo</SelectItem>
                    <SelectItem value="atacado">Atacado</SelectItem>
                    <SelectItem value="especial">Especial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <ItemsEditor items={dlg.data.items} products={products} priceType={dlg.data.price_type} onChange={(items) => setData({ items })} />
              <div>
                <label className="text-xs font-bold">Observações</label>
                <Input value={dlg.data.notes} onChange={(e) => setData({ notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg({ open: false, id: null, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="client-goal-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
